import React from "react";
import Flatpickr from "./FlatpickrSafe";
import { GenericPicker } from "../../masterdata/components/AttributePicker";
import { AttrType, FieldConfig } from "../../../types";
import LabelWithTooltip from "./CustomLabel";
import { PaymentTermsPicker } from "../../settings/PaymentTerms";
import { EasyPicker } from "./SimplePicker";
import { placeOfSupplyList } from "../../../DS-Files";
import DatePicker from "./DatePicker";


export enum StandardField {
  poNum = 'poNum',
  poDate = 'poDate',
  salesPerson = 'salesPerson',
  paymentTermsId = 'paymentTermsId',
  reason = 'reason',
  placeOfSupply = 'placeOfSupply',
  orderStatus = 'orderStatus',
  transportInfo = 'transportInfo',
  orderType = 'orderType',
  additionalInfo = 'additionalInfo'
}

interface StandardFieldProps {
  value: any;
  onChange: (value: any) => void;
  defaultValue?: any;
  onSaveAsDefault?: (value: any) => Promise<boolean>;
  showDefaultOption?: boolean;
  isEditMode?: boolean;
}

class StandardFieldComponents {
  static PoNum({ value, onChange }: StandardFieldProps) {
    return (
      <div className="form-group">
        <label className="form-label">{'PO Number'.toTranslatedDisplay()}</label>
        <input
          type="text"
          className="form-control"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={'Enter PO Number'.toTranslatedDisplay()}
        />
      </div>
    );
  }

  static AdditionalInfo({ value, onChange }: StandardFieldProps) {
    return (
      <div className="form-group">
        <label className="form-label">{'Additional Info'.toTranslatedDisplay()}</label>
        <textarea
          className="form-control"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={'Enter Additional Information'.toTranslatedDisplay()}
          rows={2}
        />
      </div>
    );
  }

  static PaymentTerms({ value, onChange, defaultValue, onSaveAsDefault, showDefaultOption }: StandardFieldProps) {
    return (
      <div className="form-group">
        <LabelWithTooltip
          labelText="Payment Terms"
          tooltipText="Payment Terms define the conditions under which a sale is completed, including when payment is due and any potential early payment discounts or late payment penalties."
        />
        <PaymentTermsPicker
          label={undefined}
          selectedValue={value}
          defaultValue={defaultValue}
          onSaveAsDefault={onSaveAsDefault}
          showDefaultOption={showDefaultOption}
          onSelectionChange={(paymentTerm) => { onChange(paymentTerm) }}
        />
      </div>
    );
  }

  static OrderStatus({ value, onChange }: StandardFieldProps) {
    return (
      <div className="form-group">
        <LabelWithTooltip labelText="Order Status" tooltipText="Order Status for tracking" />
        <GenericPicker
          categoryType={AttrType.OrderStatus}
          selectedAttribute={value}
          label={""}
          onAttributeChange={(attributeId: string) => {
            onChange(attributeId); // Pass the selected attributeId to the onChange handler
          }}
        />
      </div>
    );
  }

  static OrderType({ value, onChange }: StandardFieldProps) {
    return (
      <div className="form-group">
        <LabelWithTooltip labelText="Order Type" tooltipText="Order Type" />
        <GenericPicker
          categoryType={AttrType.OrderType}
          selectedAttribute={value}
          label={""}
          onAttributeChange={(attributeId: string) => {
            onChange(attributeId); // Pass the selected attributeId to the onChange handler
          }}
        />
      </div>
    );
  }

  static DefaultField(props: StandardFieldProps, fieldConfig: FieldConfig) {
    return (
      <div className="form-group">
        <label className="form-label">{fieldConfig.fieldHeading}</label>
        <input
          type="text"
          className="form-control"
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          placeholder={fieldConfig.fieldHeading}
        />
      </div>
    );
  }

  static PoDate({ value, onChange }: StandardFieldProps) {
    return (
      <div className="">
        <label className="form-label">{'PO Date'.toTranslatedDisplay()}</label>
        <DatePicker
          className="form-control"
          value={value}
          onChange={(date) => onChange(date)}
          placeholder={'Select PO Date'.toTranslatedDisplay()}
        />
      </div>
    );
  }

  static SalesPerson({ value, onChange }: StandardFieldProps) {
    return (
      <div className="">
        <label>{'Sales Person'.toTranslatedDisplay()}</label>
        <GenericPicker
          categoryType={AttrType.SalesPerson}
          selectedAttribute={value}
          label={""}
          onAttributeChange={(attributeId: string) => {
            onChange(attributeId); // Pass the selected attributeId to the onChange handler
          }}
        />
      </div>
    );
  }

  static Reason({ value, onChange }: StandardFieldProps) {
    return (
      <div className="">
        <label>{'Reason'.toTranslatedDisplay()}</label>
        <GenericPicker
          categoryType={AttrType.Reason}
          selectedAttribute={value}
          label={""}
          onAttributeChange={(attributeId: string) => {
            onChange(attributeId); // Pass the selected attributeId to the onChange handler
          }}
        />
      </div>
    );
  }

  static PlaceOfSupply({ value, onChange }: StandardFieldProps) {
    const options = placeOfSupplyList.map(place => place.state_nm);
    return (
      <div className="">
        <label>{'Place Of Supply'.toTranslatedDisplay()}</label>
        <EasyPicker
          value={value}
          singleValueOptions={options}
          onSelectionChange={(e) => {
            onChange(e.target.value); // Pass the selected attributeId to the onChange handler
          }}
        />
      </div>
    );
  }

  static getComponent(field: StandardField, props: StandardFieldProps, fieldConfig: FieldConfig) {
    switch (field) {
      case StandardField.poNum:
        return StandardFieldComponents.PoNum(props);
      case StandardField.poDate:
        return StandardFieldComponents.PoDate(props);
      case StandardField.salesPerson:
        return StandardFieldComponents.SalesPerson(props);
      case StandardField.paymentTermsId:
        return StandardFieldComponents.PaymentTerms(props);
      case StandardField.reason:
        return StandardFieldComponents.Reason(props);
      case StandardField.orderStatus:
        return StandardFieldComponents.OrderStatus(props);
      case StandardField.orderType:
        return StandardFieldComponents.OrderType(props);
      case StandardField.placeOfSupply:
        return StandardFieldComponents.PlaceOfSupply(props);
      case StandardField.additionalInfo:
        return StandardFieldComponents.AdditionalInfo(props);
      default:
        return StandardFieldComponents.DefaultField(props, fieldConfig)
    }
  }
}

export default StandardFieldComponents;



